import React, { useCallback, useEffect, useRef, useState } from 'react'
import { oauthCheckProductEligibility } from '@/services/checkout'
import ExternalRedeemModal from '@/pages/external/redeem/ExternalRedeemModal'
import { PremiumPaymentSession } from '@/pages/external/ExternalPremiumLogin/PremiumPaymentSession'
import { RevenueCatPurchase } from '@/pages/external/RevenuecatCheckout'
import { useOauthSubscriptions } from '@/services'
import { User } from '@/types'
import { AppButton } from '@/pages/external/components/shell'

interface SubscriptionRequiredComponentProps {
  token: string
  base64ClientKey: string
  user: User
}

type FlowState = 'prompt' | 'loading_products' | 'purchasing' | 'verifying' | 'success' | 'error'

const POLL_INTERVAL_MS = 10_000

/**
 * Shown when the login API returns requiredActions including SUBSCRIPTION_REQUIRED.
 * Guides the user through purchasing a DUPR+ subscription, then re-authenticates
 * to verify the requirement is cleared before posting credentials to the parent window.
 */
export const SubscriptionRequiredComponent: React.FC<SubscriptionRequiredComponentProps> = ({
  token,
  base64ClientKey,
  user,
}) => {
  const [flowState, setFlowState] = useState<FlowState>('loading_products')
  const [eligiblePromotions, setEligiblePromotions] = useState<string[]>([])
  const [apiError, setApiError] = useState(false)
  const [verifyError, setVerifyError] = useState<string | undefined>()

  const { mutate: fetchSubscriptions } = useOauthSubscriptions()

  // Check product eligibility on mount
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const products = await oauthCheckProductEligibility({
          token,
          request: { base64ClientKey },
        })
        setEligiblePromotions(products)
      } catch (error) {
        console.error('Failed to check product eligibility:', error)
        setApiError(true)
      } finally {
        setFlowState('prompt')
      }
    }

    checkEligibility()
  }, [token, base64ClientKey])

  // After payment completes (detected by polling), the read-only session token stays valid and the
  // subscription is now active on this account. Re-fetch subscriptions with it and hand the parent
  // the fresh list. There is NO first-party session conversion here: the iframe always logs in
  // fresh (login + 2FA), and the parent already holds the refresh token from the initial hand-off.
  const handlePaymentComplete = useCallback(() => {
    setFlowState('verifying')
    setVerifyError(undefined)

    fetchSubscriptions(
      { token },
      {
        onSuccess: (res) => {
          window.parent.postMessage(
            {
              userToken: token,
              id: user.id,
              duprId: user.referralCode,
              stats: user.stats,
              subscriptions: res.subscriptions,
            },
            '*'
          )
          setFlowState('success')
        },
        onError: (error: any) => {
          console.error('Failed to fetch subscriptions after payment:', error)
          window.parent.postMessage(
            { userToken: token, id: user.id, duprId: user.referralCode, stats: user.stats },
            '*'
          )
          setFlowState('success')
        },
      }
    )
  }, [token, user, fetchSubscriptions])

  // Poll subscription endpoint while in purchasing state to detect purchase completion
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (flowState !== 'purchasing') {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      return
    }

    pollingRef.current = setInterval(() => {
      fetchSubscriptions(
        { token },
        {
          onSuccess: (res) => {
            if (res.subscriptions && res.subscriptions.length > 0) {
              if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
              }
              handlePaymentComplete()
            }
          },
          onError: () => {
            // Silently continue polling — the endpoint may return 428 until subscription is active
          },
        }
      )
    }, POLL_INTERVAL_MS)

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [flowState, token, fetchSubscriptions, handlePaymentComplete])

  // Loading products
  if (flowState === 'loading_products') {
    return (
      <div className="flex flex-col items-center gap-y-4 p-8 w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)]" />
        <p className="text-sm text-[color:var(--ink-soft)]">Loading subscription options...</p>
      </div>
    )
  }

  // Verifying after payment
  if (flowState === 'verifying') {
    return (
      <div className="flex flex-col items-center gap-y-4 p-8 w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)]" />
        <p className="text-sm text-[color:var(--ink-soft)]">Verifying your subscription...</p>
      </div>
    )
  }

  // Success - account linked
  if (flowState === 'success') {
    return (
      <div className="flex flex-col items-center gap-y-4 p-8 w-full">
        <div className="w-16 h-16 rounded-full bg-[color:var(--positive)]/10 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-[color:var(--positive)]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="text-center">
          <p className="text-lg font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
            You have successfully linked your DUPR account!
          </p>
        </div>
      </div>
    )
  }

  // Error after payment verification
  if (flowState === 'error') {
    return (
      <div className="flex flex-col items-center gap-y-4 p-8 w-full">
        <div className="w-16 h-16 rounded-full bg-[color:var(--negative)]/10 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-[color:var(--negative)]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <p className="text-sm text-[color:var(--ink-soft)] text-center">{verifyError}</p>
        <AppButton size="md" onClick={handlePaymentComplete}>
          Try Again
        </AppButton>
      </div>
    )
  }

  // Purchasing - show the payment flow
  if (flowState === 'purchasing') {
    if (apiError || !eligiblePromotions.length) {
      return (
        <PremiumPaymentSession token={token} base64ClientKey={base64ClientKey} user={user}>
          <RevenueCatPurchase isModal={true} />
        </PremiumPaymentSession>
      )
    }
    return (
      <ExternalRedeemModal
        token={token}
        base64ClientKey={base64ClientKey}
        promotionIds={eligiblePromotions}
      />
    )
  }

  // Prompt - show the subscription required message
  return (
    <div className="flex flex-col items-center gap-y-4 p-8 w-full">
      <div className="w-16 h-16 rounded-full bg-[color:var(--warning)]/10 flex items-center justify-center">
        <svg
          className="w-8 h-8 text-[color:var(--warning)]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>

      <div className="text-center">
        <p className="text-lg font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] mb-2">
          DUPR+ Membership Required
        </p>
        <p className="text-sm text-[color:var(--ink-soft)]">
          A DUPR+ membership is required to connect your account. Subscribe to get access to
          exclusive features, tournament access, and more.
        </p>
      </div>

      <AppButton size="md" onClick={() => setFlowState('purchasing')}>
        Subscribe Now
      </AppButton>
    </div>
  )
}
